import type { OrderListDto, OrderStatusUpdateDto } from "@/types/order.dto";
import { DeliveryType, OrderStatus, PaymentMethod, PaymentStatus } from "@/types/enums";
import type { AccountOrderRow } from "@/types/account.ui";
import type { AdminOrder } from "@/types/admin.ui";

export function mapOrderStatusToUi(
  status: OrderStatus,
): AccountOrderRow["status"] {
  switch (status) {
    case OrderStatus.Pending:
      return "placed";
    case OrderStatus.Confirmed:
      return "confirmed";
    case OrderStatus.Processing:
    case OrderStatus.ReadyForPickup:
    case OrderStatus.OutForDelivery:
      return "dispatched";
    case OrderStatus.Delivered:
      return "delivered";
    case OrderStatus.Cancelled:
      return "cancelled";
    default:
      return "placed";
  }
}

export function mapUiStatusToOrderStatus(status: string): OrderStatus | undefined {
  switch (status) {
    case "placed":
      return OrderStatus.Pending;
    case "confirmed":
      return OrderStatus.Confirmed;
    case "dispatched":
      return OrderStatus.OutForDelivery;
    case "delivered":
      return OrderStatus.Delivered;
    case "cancelled":
      return OrderStatus.Cancelled;
    default:
      return undefined;
  }
}

export function mapPaymentMethodToUi(method: PaymentMethod): AccountOrderRow["paymentMethod"] {
  switch (method) {
    case PaymentMethod.COD:
      return "cod";
    case PaymentMethod.Wallet:
      return "wallet";
    case PaymentMethod.Online:
    default:
      return "razorpay";
  }
}

export function formatPaymentMethodLabel(
  method: AccountOrderRow["paymentMethod"] | "online" | string,
): string {
  switch (method) {
    case "cod":
      return "COD";
    case "wallet":
      return "Wallet";
    case "razorpay":
    case "online":
      return "Razorpay";
    default:
      return String(method);
  }
}

export function mapDeliveryTypeToUi(type: DeliveryType): AccountOrderRow["deliveryType"] {
  return type === DeliveryType.StoreDelivery ? "store" : "platform";
}

export function formatOrderDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function mapOrderListDtoToAccountRow(dto: OrderListDto): AccountOrderRow {
  return {
    id: String(dto.id),
    orderId: dto.orderNumber,
    storeName: dto.storeName,
    itemCount: dto.itemCount,
    total: dto.totalAmount,
    status: mapOrderStatusToUi(dto.status),
    deliveryType: mapDeliveryTypeToUi(dto.deliveryType),
    orderDate: formatOrderDate(dto.createdAt),
    paymentMethod: mapPaymentMethodToUi(dto.paymentMethod),
  };
}

export function mapOrderListDtoToAdminOrder(dto: OrderListDto): AdminOrder {
  const paymentMethod = mapPaymentMethodToUi(dto.paymentMethod);
  return {
    id: String(dto.id),
    orderId: dto.orderNumber,
    customerName: dto.customerName,
    storeName: dto.storeName,
    items: dto.itemCount,
    total: dto.totalAmount,
    status: mapOrderStatusToUi(dto.status),
    deliveryType: mapDeliveryTypeToUi(dto.deliveryType),
    orderDate: formatOrderDate(dto.createdAt),
    paymentMethod,
    paymentMethodText: dto.paymentMethodText || formatPaymentMethodLabel(paymentMethod),
  };
}

/** @deprecated Prefer mapPaymentMethodToUi — kept for callers that only know payment status. */
export function mapPaymentMethodFromPaymentStatus(status: PaymentStatus): "cod" | "razorpay" {
  return status === PaymentStatus.Completed ? "razorpay" : "cod";
}

export function buildOrderStatusUpdate(status: AdminOrder["status"]): OrderStatusUpdateDto {
  const mapped = mapUiStatusToOrderStatus(status) ?? OrderStatus.Pending;
  return { status: mapped };
}

export function mapOrderListDtosToAccountRows(items: OrderListDto[]): AccountOrderRow[] {
  return items.map(mapOrderListDtoToAccountRow);
}

export function mapOrderListDtosToAdminOrders(items: OrderListDto[]): AdminOrder[] {
  return items.map(mapOrderListDtoToAdminOrder);
}
